import { useEffect, useMemo, useContext, useState, Fragment } from 'react';
import {
  MaterialReactTable,
  useMaterialReactTable,
} from 'material-react-table';
import classes from './DashboardTable.module.css'
import useLoader from '../../hooks/useLoader';
import Snackbar from '@mui/material/Snackbar';
import Loader from "../UI/Loader";
import Alert from '@mui/material/Alert';
import { useSnackbar } from 'notistack';
import { getToken, getMSID, getEmail, getName } from "../../authfile";
import { UserContext } from "../../hooks/UserContext";
import { useGetDashboardData, useGetDashboardDataOfAuditor } from '../../queries/useGetDashboardData';
import { Autocomplete, FormControl, InputLabel, MenuItem, Select, TextField } from '@mui/material';
import { ADMIN, TECHNICIAN } from '../../constants/routeConstants';
import { isError } from 'react-query';
import WithConditional from '../hoc/WithConditional';
import CustomizedSnackbars from '../UI/SnackBar';



const DashboardTable = () => {
  //should be memoized or stable
  const tokenConfig = {
    headers: {
        "Authorization": `Bearer ${getToken()}`,
    },
  };
  const [loader, showLoader, hideLoader] = useLoader();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const { userRole } = useContext(UserContext);
  const [auditorMsid, setAuditorMsid] = useState('');
  const userDetails = {
    name: getName(),
    email: getEmail(),
    msid: getMSID(),
    role: userRole,
  }
  const {
    data: dashboardData = [],
    isError: isDashboardDataError,
    error: dashboardDataError,
    isFetching: isFetchingDashboardData,
  } = useGetDashboardData({body: userDetails, config: tokenConfig});

  const {
    data: dashboardDataOfAuditor = [],
    isError: isDashboardDataOfAuditorError,
    error: dashboardDataOfAuditorError,
    isFetching: isFetchingdashboardDataOfAuditor,
    refetch: refetchAuditorDashboard
  } = useGetDashboardDataOfAuditor({body: {
    ...userDetails,
    msid: auditorMsid,
    role: TECHNICIAN
  }, config: tokenConfig});

  //console.log("config is", getToken());


  const columns = useMemo(
    () => [
      {
        accessorKey: 'ReportName', //access nested data with dot notation
        header: 'Reports',
        size: 80,
      },
      {
        accessorKey: 'Past Due SLA',
        header: 'Past Due SLA',
        Cell: ({ cell }) => {
          const val2 = cell.getValue();
          return val2 ? new Intl.NumberFormat('en-US').format(val2) : "";
        },
        size: 30,
      },
      {
        accessorKey: 'SLA due Today', //normal accessorKey
        header: 'SLA due Today',
        size: 30,
        Cell: ({ cell }) => {
          const val2 = cell.getValue();
          return val2 ? new Intl.NumberFormat('en-US').format(val2) : "";
        },
      },
      {
        accessorKey: 'SLA due Tomorrow',
        header: 'SLA due Tomorrow',
        size: 40,
        Cell: ({ cell }) => {
          const val2 = cell.getValue();
          return val2 ? new Intl.NumberFormat('en-US').format(val2) : "";
        },
      },
      {
        accessorKey: 'SLA due 2+ days',
        header: 'SLA due 2+ days',
        size: 30,
        Cell: ({ cell }) => {
          const val2 = cell.getValue();
          return val2 ? new Intl.NumberFormat('en-US').format(val2) : "";
        },
      },
    ],
    [],
  );

  const tableOfDashboard = useMaterialReactTable({
    columns,
    data: Object.values(dashboardData?.gridData || []), //data must be memoized or stable (useState, useMemo, defined outside of this component, etc.)
    enableTopToolbar: false,
    enablePagination: false,
    muiTableProps: {
      sx: {
        tableLayout: 'fixed',
      }
    },
    muiTableHeadCellProps: ({ row }) => ({
      sx: {
        backgroundColor: '#d9f6fa',
        padding: '0.5rem 0.2rem',
      }
    }),
    muiTableBodyCellProps: ({ row }) => {
      return ({
        sx: {
          backgroundColor: row.original['ReportName'] === 'Total' ? '#d9f6fa' : 'white',
          padding: '1rem 0.5rem',
          fontSize: '0.8rem'
        }
      })
    }
  });

  const tableOfAuditorDashboard = useMaterialReactTable({
    columns,
    data: Object.values(dashboardDataOfAuditor?.gridData || []), //data must be memoized or stable (useState, useMemo, defined outside of this component, etc.)
    enableTopToolbar: false,
    enablePagination: false,
    muiTableProps: {
      sx: {
        tableLayout: 'fixed',
      }
    },
    muiTableHeadCellProps: ({ row }) => ({
      sx: {
        backgroundColor: '#d9f6fa',
        padding: '0.5rem 0.2rem',
      }
    }),
    muiTableBodyCellProps: ({ row }) => {
      return ({
        sx: {
          backgroundColor: row.original['ReportName'] === 'Total' ? '#d9f6fa' : 'white',
          padding: '1rem 0.5rem',
          fontSize: '0.8rem'
        }
      })
    }
  });


  useEffect(() => {
    //console.log("tableOfDashboard selected rows are,............|||||............", tableOfDashboard.getFilteredRowModel().rows);
  }, [tableOfDashboard.getFilteredRowModel().rows]);

  return <div className={classes.reportTable}>

    <WithConditional when={isDashboardDataError}>
      <CustomizedSnackbars type="error" API="Dashboard Table API" message={dashboardDataError?.message} />
    </WithConditional>
    <WithConditional when={isDashboardDataOfAuditorError}>
      <CustomizedSnackbars type="error" API="Dashboard Table API" message={dashboardDataOfAuditorError?.message} />
    </WithConditional>
    {(isFetchingDashboardData || isFetchingdashboardDataOfAuditor) && <Loader />}
    <MaterialReactTable table={tableOfDashboard} />
    {
      userRole == ADMIN && <Fragment>
        <div className={classes.auditorDropdown}>
          <Autocomplete
            disablePortal
            disableClearable
            options={dashboardData?.userList}
            onChange={(event, newValue) => {
              setAuditorMsid(newValue.split("/")[1])
            }}
            sx={{ width: 300 }}
            renderInput={(params) => <TextField {...params} label="Select Auditor" />}
          />
        </div>
        <MaterialReactTable table={tableOfAuditorDashboard} />
      </Fragment>
    }
  </div>;
};

export default DashboardTable;
